/**
 * API protection rule — read-only detail. Reached from the list (row click / view)
 * and after create, via `api-protection/detail?domain=&id=`.
 *
 * Shows the rule's match + token settings ("Rule details") and the per-language
 * "Implement script" the customer runs on their auth server to mint a signed,
 * time-limited token for the custom parameter. The scripts are built client-side
 * from the rule's `key` + `param` (`apiProtectionScripts.ts`); the backend does
 * not return them. Editing lives in `WafApiProtectionForm`.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound, Pencil, X } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CodeBlock,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useWafFirewallSchema,
  useWafApiProtectionRules,
  type APIProtectionRule,
  type FirewallField,
} from '@/api/waf';
import { buildApiProtectionScripts } from './apiProtectionScripts';
import styles from './WafApiProtection.module.css';

/** The fixed match field — request URI without query string (mirrors the form). */
const URI_FIELD = 'URI:match_all';

/** One label/value pair in the Rule details table. */
function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <div className={styles.detailKey}>{label}</div>
      <div className={styles.detailVal}>{value}</div>
    </>
  );
}

function DetailBody({ serviceId, domain, rule }: { serviceId: string; domain: string; rule: APIProtectionRule }) {
  const navigate = useNavigate();
  const back = () => navigate(`/services/waf/${serviceId}/api-protection`);
  const edit = () =>
    navigate(`/services/waf/${serviceId}/api-protection/edit?domain=${encodeURIComponent(domain)}&id=${encodeURIComponent(rule.id)}`);

  // Resolve human labels for the match field + operator from the firewall schema.
  const schemaQ = useWafFirewallSchema(serviceId);
  const fields = schemaQ.data?.fields ?? [];
  const uriField = useMemo<FirewallField | undefined>(
    () => fields.find((f) => f.value === URI_FIELD) ?? fields.find((f) => /uri/i.test(f.value) || /uri/i.test(f.label)),
    [fields],
  );
  const cond = useMemo(
    () => (rule.conditions ?? []).find((c) => c.field === (uriField?.value ?? URI_FIELD)) ?? (rule.conditions ?? [])[0],
    [rule.conditions, uriField],
  );
  const fieldLabel = uriField?.label ?? cond?.field ?? '—';
  const operatorLabel =
    uriField?.operators.find((o) => o.value === cond?.operator)?.label ?? cond?.operator ?? '—';

  const a = rule.action;
  const scripts = useMemo(() => buildApiProtectionScripts(a.key, a.param), [a.key, a.param]);
  const [tab, setTab] = useState(scripts[0]?.lang ?? 'python');

  return (
    <Card>
      <CardHeader
        actions={
          <div style={{ display: 'inline-flex', gap: 'var(--vn-space-2)' }}>
            <Button variant="secondary" startIcon={<Pencil size={15} />} onClick={edit}>Edit</Button>
            <Button variant="secondary" startIcon={<X size={15} />} onClick={back}>Close</Button>
          </div>
        }
      >
        <CardTitle>API protection rule - {rule.name}</CardTitle>
        <CardDescription>
          Customise and configure your own API protection rule to keep your API requests secured.
        </CardDescription>
      </CardHeader>
      <CardBody>
        <h3 className={styles.sectionTitle}>Rule details</h3>
        <div className={styles.detailTable}>
          <DetailRow label="Field" value={fieldLabel} />
          <DetailRow label="Operators" value={operatorLabel} />
          <DetailRow label="Value" value={cond?.value || '—'} />
          <DetailRow label="Key" value={<span style={{ fontFamily: 'var(--vn-font-mono)' }}>{a.key || '—'}</span>} />
          <DetailRow label="Token lifespan" value={a.token_ttl ?? '—'} />
          <DetailRow label="Custom parameter" value={<span style={{ fontFamily: 'var(--vn-font-mono)' }}>{a.param || '—'}</span>} />
          {a.rate_limit_enable && (
            <>
              <DetailRow label="Rate limit" value={`${a.rate_limit_queries} / min`} />
              <DetailRow label="Block time" value={`${a.rate_limit_block_time} sec`} />
            </>
          )}
        </div>

        <div className={styles.scriptSection}>
          <h3 className={styles.sectionTitle}>Implement script</h3>
          <p className={styles.scriptIntro}>
            The following script will generate a token that will expire. Copy the script below and set it up on your server.
          </p>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              {scripts.map((s) => (
                <TabsTrigger key={s.lang} value={s.lang}>{s.label}</TabsTrigger>
              ))}
            </TabsList>
            {scripts.map((s) => (
              <TabsContent key={s.lang} value={s.lang}>
                <CodeBlock code={s.code} copyable maxHeight={420} />
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </CardBody>
    </Card>
  );
}

export function WafApiProtectionDetail({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const id = params.get('id') ?? '';
  const back = () => navigate(`/services/waf/${serviceId}/api-protection`);

  const header = (
    <PageHeader
      icon={<KeyRound size={22} />}
      title="API protection"
      description="You can set up API protection rules through a time-limited authentication token. API rules, token settings, and authentication server scripts can be customized based on your security requirements."
    />
  );

  const rulesQ = useWafApiProtectionRules(serviceId, domain || undefined);
  const rule = useMemo(() => (rulesQ.data ?? []).find((r) => r.id === id), [rulesQ.data, id]);

  if (!domain || !id) {
    return (
      <Container size="lg">{header}
        <Alert tone="warning" title="No rule selected" actions={<button onClick={back}>Back</button>}>
          Open this page from the API Protection list.
        </Alert>
      </Container>
    );
  }
  if (rulesQ.isError) {
    return (
      <Container size="lg">{header}
        <Alert tone="danger" title="Couldn't load the rule" actions={<button onClick={() => rulesQ.refetch()}>Retry</button>}>
          The WAF service couldn't return this rule. Try again.
        </Alert>
      </Container>
    );
  }
  if (rulesQ.isLoading) {
    return <Container size="lg">{header}<Skeleton style={{ height: 520 }} /></Container>;
  }
  if (!rule) {
    return (
      <Container size="lg">{header}
        <Alert tone="danger" title="Rule not found" actions={<button onClick={back}>Back</button>}>This rule no longer exists.</Alert>
      </Container>
    );
  }

  return (
    <Container size="lg">
      {header}
      <DetailBody serviceId={serviceId} domain={domain} rule={rule} />
    </Container>
  );
}

export default WafApiProtectionDetail;
